import React, { useState, useEffect } from "react";
import { PageLoader } from "@vionixasia/shared-components";
import { BrowserRouter as Router, Routes, Route, useParams, Navigate } from "react-router-dom";
import { AuthProvider, Login, Register, Verify, LupaPassword } from "@vionixasia/shared-components";
import { NotificationProvider } from "@vionixasia/shared-components/common/NotificationContext";
import { AdminRoute } from "./components/AdminRoute";
import { Layout } from "./components/Layout";
import { EventProvider, useEventId } from "./EventContext";

// Pages
import LandingPage from "./pages/LandingPage";
import { SMM } from "./pages/Results";
import { ResultsAge } from "./pages/ResultsAge";
import { InputBIB } from "./pages/InputBIB";
import { InputBIBLoop } from "./pages/InputBIBLoop";
import { InputNinja } from "./pages/InputNinja";
import { StartWave } from "./pages/StartWave";
import { EventLog } from "./pages/EventLog";
import { Announcer } from "./pages/Announcer";
import { VenueDisplay } from "./pages/VenueDisplay";
import { AquaFitXBoard } from "./pages/AquaFitXBoard";
import { AquaFitXStartlist } from "./pages/AquaFitXStartlist";
import { useEventTheme } from "./utils/useEventTheme";
import { PhotoGate } from "./pages/PhotoGate";
import { Admintiming } from "./pages/AdminTiming";
import { Profile } from "@vionixasia/shared-components";

import ResultsEditor from "./pages/ResultsEditor";
import { ResultsLoop } from "./pages/ResultsLoop";
import StartlistSMM from "./pages/StartlistSMM";
import Startlist from "./pages/Startlist";
import ResultsDetail from "./pages/ResultsDetail";

/**
 * An event with its own theme (`theme` on its event document, e.g. "aquafitx")
 * gets its own display board and start list; every other event the generic
 * ones.
 */
const DisplayRoute = () => {
  const { realEventId: eventId } = useEventId();
  const theme = useEventTheme(eventId);
  if (!theme) return <PageLoader />;
  return theme === "aquafitx" ? <AquaFitXBoard /> : <VenueDisplay />;
};

const StartlistRoute = () => {
  const { realEventId: eventId } = useEventId();
  const theme = useEventTheme(eventId);
  if (!theme) return <PageLoader />;
  // The themed page is full-bleed with no site navbar, like the display board;
  // every other start list keeps the site shell.
  return theme === "aquafitx" ? <AquaFitXStartlist /> : <Layout><Startlist /></Layout>;
};

const EventRoot = () => {
  const { realEventId: eventId } = useEventId();
  const [eventMeta, setEventMeta] = useState({
    status: "loading",
    hasLooping: false,
    data: null,
  });

  useEffect(() => {
    let isActive = true;

    const fetchEventMeta = async () => {
      setEventMeta((prev) => ({ ...prev, status: "loading" }));
      try {
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/timing/event_timing`);
        const data = await response.json();
        if (!isActive) return;

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const eventData = data.find((e) => e.eventId === eventId);
        if (eventData) {
          const hasLoopingCategory = (eventData.categories || []).some((cat) => cat.isLooping);
          setEventMeta({
            status: "ready",
            hasLooping: hasLoopingCategory,
            data: eventData,
          });
        } else {
          setEventMeta({
            status: "ready",
            hasLooping: false,
            data: null,
          });
        }
      } catch (error) {
        if (!isActive) return;
        console.error("[EventRoot] Failed to fetch event metadata:", error);
        setEventMeta({
          status: "ready",
          hasLooping: false,
          data: null,
        });
      }
    };

    fetchEventMeta();
    return () => {
      isActive = false;
    };
  }, [eventId]);

  if (eventMeta.status === "loading") {
    return <PageLoader />;
  }

  const componentProps = eventMeta.data ? { initialEventData: eventMeta.data } : {};

  if (eventMeta.hasLooping) {
    return <ResultsLoop {...componentProps} />;
  }

  // Age-group results are a property of the event, set in admin. The event id
  // was matched by name here, so every new age-graded event needed a code
  // change; the name check stays only so events saved before the flag existed
  // keep working.
  const wantsAgeGroup =
    eventMeta.data?.enableAgeGroup === true ||
    (eventId && eventId.includes("indonesia_masters"));

  if (wantsAgeGroup) {
    return <ResultsAge {...componentProps} />;
  }

  return <SMM {...componentProps} />;
};

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <Routes>

            <Route path="/StartlistSMM" element={<StartlistSMM />} />
            {/* Removed standalone entry-list route here as it is moved inside Layout for consistent resolving */}
          
          {/* InputBIB - NO Layout but needs EventProvider */}
          {/* Open to anyone: volunteers at water stations input BIBs without an account. */}
          <Route path=":eventId/input" element={<EventProvider><InputBIB /></EventProvider>} />
          <Route path=":eventId/input-loop" element={<EventProvider><InputBIBLoop /></EventProvider>} />

          {/* Announcer - full screen for the MC at the finish line, no Layout */}
          <Route path=":eventId/announcer" element={<EventProvider><Announcer /></EventProvider>} />
          {/* Start list: the event's themed page (no navbar) or the generic one in the site shell */}
          <Route path=":eventId/startlist" element={<EventProvider><StartlistRoute /></EventProvider>} />
          {/* The event's venue screen: the generic board, or its themed one */}
          <Route path=":eventId/display" element={<EventProvider><DisplayRoute /></EventProvider>} />
          {/* Direct link to the AquaFitX board, kept so links already shared still work */}
          <Route path=":eventId/live" element={<EventProvider><AquaFitXBoard /></EventProvider>} />
          {/* Photo/TV board behind the finish line, fed by a Post-Finish Gate mat */}
          <Route path=":eventId/gate" element={<EventProvider><PhotoGate /></EventProvider>} />
          
          {/* All other routes WITH Layout */}
          <Route path="/" element={<Layout />}>
            <Route index element={<LandingPage />} />
            


            
            <Route path="profile" element={<Profile />} />
            <Route path="Profile" element={<Profile />} />
            
            {/* Dynamic Event Routes - Wrapped in EventProvider for Slug Resolution */}
            <Route path=":eventId" element={<EventProvider><EventRoot /></EventProvider>} />
            <Route path=":eventId/results" element={<EventProvider><SMM /></EventProvider>} />
            <Route path=":eventId/results-loop" element={<EventProvider><ResultsLoop /></EventProvider>} />
            <Route path=":eventId/age-results" element={<EventProvider><ResultsAge /></EventProvider>} />
            <Route path=":eventId/:bib" element={<EventProvider><ResultsDetail /></EventProvider>} />
            {/* <Route path=":eventId/startlist" element={<EventProvider><StartlistSMM /></EventProvider>} /> */ }
            
            {/* Start list: see StartlistRoute, registered outside this Layout group */}

            {/* Protected Admin Routes */}
            <Route
              path="admin"
              element={
                <AdminRoute>
                  <Admintiming />
                </AdminRoute>
              }
            />
            <Route
              path=":eventId/input-ninja"
              element={
                <AdminRoute>
                  <EventProvider>
                    <InputNinja />
                  </EventProvider>
                </AdminRoute>
              }
            />
            <Route
              path=":eventId/start"
              element={
                <AdminRoute>
                  <EventProvider>
                    <StartWave />
                  </EventProvider>
                </AdminRoute>
              }
            />
            <Route
              path=":eventId/log"
              element={
                <AdminRoute>
                  <EventProvider>
                    <EventLog />
                  </EventProvider>
                </AdminRoute>
              }
            />
            <Route
              path=":eventId/editor"
              element={
                <AdminRoute>
                  <EventProvider>
                    <ResultsEditor />
                  </EventProvider>
                </AdminRoute>
              }
            />
          </Route>
          
          {/* Auth Routes Using Shared Components (Diluar Layout) */}
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="verify" element={<Verify />} />
          <Route path="lupapassword" element={<LupaPassword />} />
        </Routes>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
